import { filterJobs, filterJobsCount } from '@/api/jobs'
import { computed, ref, Ref, unref, watch } from 'vue'
import { Query, useQuery } from '@tanstack/vue-query'
import { AxiosError, AxiosResponse } from 'axios'
import { PaginatedResponse } from '@/api/types/paginated-response'
import { JobSearchCountResponse, JobSearchResponse } from '@/models/types/job-search-response'
import { FilterJobRequest } from '@/api/types/filter-job-request'
import { currentTimezone } from '@/utils/date-formatting'
import type { JobFilter, JobFilterValue, JobListSort } from '@ironsight/types'
import { FilteredSearchJob } from '@/models/types/filtered-search-job'
import {
  getSelectAllExcludedScopes,
  getSelectAllExcludedSearchTexts,
  getSelectAllExclusions,
  getSelectAllInclusions,
  getSelectAllScopes,
  getSelectAllSearchText,
  HAS_VALUE_SENTINEL,
  isSelectAllValue,
  LOCATION_FILTER_IDS,
} from './useFilterChips'

export const JOBS_FILTERED_QUERY_KEY = 'job_filtered'
export const JOBS_FILTERED_COUNT_QUERY_KEY = 'jobs_count'
export const DEFAULT_JOBS_REFETCH_INTERVAL_MS = 60_000
export type JobsQuerySource = 'direct' | 'interval'
type IntersectableSet<T> = Set<T> & {
  intersection(other: ReadonlySet<T>): Set<T>
}

export function useFilterJobsQuery(
  filters: Ref<JobFilter[]>,
  sorting: Ref<JobListSort>,
  page: Ref<number>,
  itemsPerPage: Ref<number>,
  bucketId: Ref<string>,
  refetchInterval: (
    data: AxiosResponse<PaginatedResponse<JobSearchResponse>> | undefined,
    query: Query<AxiosResponse<PaginatedResponse<JobSearchResponse>>, AxiosError>,
  ) => number | false,
  options?: { enabled: boolean | Ref<boolean> },
) {
  const enabled = computed(() => unref(options?.enabled) ?? true)
  const queryKey = computed(() => [
    JOBS_FILTERED_QUERY_KEY,
    serializeFiltersForQueryKey(filters.value),
    sorting.value.field,
    sorting.value.direction,
    page.value,
    itemsPerPage.value,
    bucketId.value,
    currentTimezone(),
  ])
  const nextRequestSource = ref<JobsQuerySource>('direct')

  watch(queryKey, () => {
    nextRequestSource.value = 'direct'
  })

  const queryResult = useQuery<AxiosResponse<PaginatedResponse<JobSearchResponse>>, AxiosError>({
    queryKey,
    queryFn: async ({ signal }) => {
      const source = nextRequestSource.value
      nextRequestSource.value = 'direct'

      const response = await filterJobs(
        prepareFiltersForFetch(filters.value),
        sorting.value.field,
        sorting.value.direction === 'desc',
        currentTimezone(),
        (page.value - 1) * itemsPerPage.value,
        itemsPerPage.value,
        bucketId.value,
        signal,
        source,
      )

      return response.data.data.map((job) => new FilteredSearchJob(job))
    },
    retry: false,
    keepPreviousData: true,
    enabled,
    staleTime: DEFAULT_JOBS_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchInterval: (data, query) => {
      const interval = enabled.value ? refetchInterval(data, query) : false
      if (query.state.fetchStatus === 'idle') {
        nextRequestSource.value = interval !== false && data !== undefined ? 'interval' : 'direct'
      }

      return interval
    },
  })

  const refetch: typeof queryResult.refetch = (...args) => {
    nextRequestSource.value = 'direct'
    return queryResult.refetch(...args)
  }

  return {
    ...queryResult,
    refetch,
  }
}

export function useFilterJobsCountQuery(
  filters: Ref<JobFilter[]>,
  bucketId: Ref<string>,
  refetchInterval: (
    data: AxiosResponse<JobSearchCountResponse> | undefined,
    query: Query<AxiosResponse<JobSearchCountResponse>, AxiosError>,
  ) => number | false,
  options?: { enabled: boolean | Ref<boolean> },
) {
  const enabled = computed(() => unref(options?.enabled) ?? true)
  const queryKey = computed(() => [
    JOBS_FILTERED_COUNT_QUERY_KEY,
    serializeFiltersForQueryKey(filters.value),
    bucketId.value,
    currentTimezone(),
  ])
  return useQuery<AxiosResponse<JobSearchCountResponse>, AxiosError>({
    queryKey,
    queryFn: ({ signal }) =>
      filterJobsCount(
        prepareFiltersForFetch(filters.value),
        bucketId.value,
        currentTimezone(),
        signal,
      ),
    refetchInterval: (data, query) => (enabled.value ? refetchInterval(data, query) : false),
    retry: false,
    keepPreviousData: true,
    enabled,
    staleTime: DEFAULT_JOBS_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  })
}

const normalizeFilterValue = (value: JobFilterValue): unknown => {
  if (value instanceof Date) {
    return value.toISOString()
  }

  if (Array.isArray(value)) {
    return value.map((entry: JobFilterValue) => normalizeFilterValue(entry))
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>

    if ('value' in record || 'label' in record) {
      return {
        ...(record.label !== undefined ? { label: record.label } : {}),
        ...(record.value !== undefined
          ? { value: normalizeFilterValue(record.value as JobFilterValue) }
          : {}),
        ...(typeof record.searchText === 'string' ? { searchText: record.searchText } : {}),
        ...(Array.isArray(record.excludedSearchTexts)
          ? { excludedSearchTexts: record.excludedSearchTexts }
          : {}),
        ...(Array.isArray(record.scopes) ? { scopes: normalizeFilterValue(record.scopes) } : {}),
        ...(Array.isArray(record.excludedScopes)
          ? { excludedScopes: normalizeFilterValue(record.excludedScopes) }
          : {}),
        ...(typeof record.selectionMode === 'string'
          ? { selectionMode: record.selectionMode }
          : {}),
      }
    }

    return Object.keys(record)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = normalizeFilterValue(record[key] as JobFilterValue)
          return acc
        },
        {} as Record<string, unknown>,
      )
  }

  return value
}

export const serializeFiltersForQueryKey = (filters: JobFilter[]) =>
  filters.map((filter) => ({
    id: filter.id,
    property: filter.property,
    type: filter.type,
    comparison: filter.comparison,
    hidden: filter.hidden ?? false,
    key: filter.key ?? null,
    taskIds: filter.taskIds ?? null,
    value: normalizeFilterValue(filter.value),
  }))

const serializeLookupValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value)
}

/**
 * Prepares filters for the API call by applying business rules to determine
 * which filters should be included in the request
 *
 * @param allFilters - All currently active filters
 * @returns Filtered list of property filters ready for API request
 */
export function prepareFiltersForFetch(allFilters: JobFilter[]): FilterJobRequest[] {
  const validFilters = allFilters.filter((filter) => {
    // Always include text filters
    if (filter.type === 'Text') {
      return true
    }
    // Skip empty string values unless explicitly filtering for empty
    if (typeof filter.value === 'string' && filter.value.trim() === '') {
      return filter.comparison === 'IsEmpty'
    }
    // Handle arrays
    if (Array.isArray(filter.value)) {
      // Skip empty selection arrays
      if (filter.value.length === 0) {
        return false
      }
      // Skip date ranges where any date is null
      if (
        filter.type === 'DateTime' &&
        filter.comparison === 'Between' &&
        (filter.value as JobFilterValue[]).some((v) => v === null)
      ) {
        return false
      }
    }
    // Skip null or undefined values explicitly (if not handled above)
    return !(filter.value === null || filter.value === undefined)
  })

  // Transform select-all sentinel values:
  // - No keyword and no exclusions -> omit filter entirely (select all = no constraint)
  // - Keyword -> emit a Contains constraint for all matching rows, including unloaded pages
  // - Exclusions -> emit a NotIn constraint with only the deselected IDs
  const filtersWithSelectAllResolved = validFilters.reduce<JobFilter[]>((acc, filter) => {
    if (!isSelectAllValue(filter.value)) {
      acc.push(filter)
      return acc
    }

    const searchText = getSelectAllSearchText(filter.value)
    const exclusions = getSelectAllExclusions(filter.value)
    const excludedSearchTexts = getSelectAllExcludedSearchTexts(filter.value)
    const inclusions = getSelectAllInclusions(filter.value)
    const scopes = getSelectAllScopes(filter.value)
    const excludedScopes = getSelectAllExcludedScopes(filter.value)

    // TODO: Will need to update the backend for Batch (`ApplyBatchFilter`) before
    // we can enable Select All for the Batch Filter.
    const supportsStructuredLookupSelectAll =
      filter.property === 'ProjectId' ||
      filter.property === 'TaskId' ||
      LOCATION_FILTER_IDS.includes(filter.property)

    const hasLocationScopes =
      LOCATION_FILTER_IDS.includes(filter.property) &&
      (scopes.length > 0 || excludedScopes.length > 0)
    const useStructuredLookupSelectAll =
      supportsStructuredLookupSelectAll &&
      (excludedSearchTexts.length > 0 ||
        Boolean(filter.property === 'TaskId' && searchText) ||
        Boolean(searchText && inclusions.length > 0) ||
        hasLocationScopes)

    if (searchText || excludedSearchTexts.length > 0 || hasLocationScopes) {
      if (useStructuredLookupSelectAll) {
        const hasEmptyExcl = exclusions.some((option) => option.value === '')
        const structuredExclusions = hasEmptyExcl
          ? exclusions.filter((option) => option.value !== HAS_VALUE_SENTINEL)
          : exclusions
        acc.push({
          ...filter,
          comparison: 'Contains',
          value: JSON.stringify({
            ...(searchText ? { searchText } : {}),
            excludedValues: structuredExclusions.map((option) =>
              serializeLookupValue(option.value),
            ),
            includedValues: inclusions.map((option) => serializeLookupValue(option.value)),
            ...(excludedSearchTexts.length > 0 ? { excludedSearchTexts } : {}),
            ...(scopes.length > 0 ? { scopes } : {}),
            ...(excludedScopes.length > 0 ? { excludedScopes } : {}),
          }),
        })
      } else if (searchText) {
        acc.push({
          ...filter,
          comparison: 'Contains',
          value: searchText,
        })
      }
    }

    // When both '' (Is Empty) and __HAS_VALUE__ (Is Not Empty) are
    // excluded together the backend produces an impossible WHERE clause
    // (ProjectId != null AND ProjectId == null) that returns zero rows.
    // Strip __HAS_VALUE__ only when the empty marker is also present so
    // the contradiction is resolved while preserving the user's intent
    // when __HAS_VALUE__ is the sole or independent deselection.
    const hasEmptyExclusion = exclusions.some((option) => option.value === '')
    const notInExclusions = hasEmptyExclusion
      ? exclusions.filter((option) => option.value !== HAS_VALUE_SENTINEL)
      : exclusions
    if (notInExclusions.length > 0 && !useStructuredLookupSelectAll) {
      acc.push({
        ...filter,
        comparison: 'NotIn',
        value: notInExclusions,
      })
    }

    if (!searchText && inclusions.length > 0 && !useStructuredLookupSelectAll) {
      acc.push({
        ...filter,
        comparison: 'In',
        value: inclusions,
      })
    }

    return acc
  }, [])

  const processedFilters = filtersWithSelectAllResolved.map((filter) => {
    let valueString: string
    const isDateTime = filter.type === 'DateTime'
    const isLookup = filter.type === 'StringLookup' || filter.type === 'IntLookup'
    const isCustomStringLookup = filter.property === 'CustomData' && filter.type === 'StringLookup'

    if (isDateTime) {
      if (
        filter.comparison === 'Between' &&
        Array.isArray(filter.value) &&
        filter.value.length === 2
      ) {
        const [start, end] = filter.value as [Date | string, Date | string]
        // Handle both Date objects and ISO strings
        const startDate = start instanceof Date ? start : new Date(start)
        const endDate = end instanceof Date ? end : new Date(end)
        valueString = `${startDate.toISOString()},${endDate.toISOString()}`
      } else {
        // Handle single date value (Date object or ISO string)
        if (filter.value instanceof Date) {
          valueString = filter.value.toISOString()
        } else if (typeof filter.value === 'string') {
          valueString = new Date(filter.value).toISOString()
        } else {
          valueString = ''
        }
      }
    } else if (isCustomStringLookup && Array.isArray(filter.value)) {
      valueString = JSON.stringify(
        filter.value.map((item) =>
          typeof item === 'object' && item !== null && 'value' in item ? item.value : item,
        ),
      )
    } else if (isLookup && Array.isArray(filter.value)) {
      valueString = filter.value
        .map((item) =>
          typeof item === 'object' && item !== null && 'value' in item ? item.value : item,
        )
        .join(',')
    } else if (
      filter.property === 'CustomData' &&
      filter.type === 'Text' &&
      filter.comparison === 'Equals'
    ) {
      // Custom data text Equals values must be JSON-quoted so the backend's
      // json_contains call receives a valid JSON string argument.
      valueString = JSON.stringify(filter.value.toString())
    } else {
      valueString = filter.value.toString()
    }

    const filterRequest: FilterJobRequest = {
      property: filter.property,
      kind: filter.type,
      operation: filter.comparison,
      value: valueString,
      label: filter.label,
      key: filter.key,
    }
    return filterRequest
  })

  const allCustomDataTaskIds = new Set<number>()

  allFilters.forEach((filter) => {
    if (filter.taskIds && Array.isArray(filter.taskIds) && filter.taskIds.length > 0) {
      filter.taskIds.forEach((taskId) => {
        allCustomDataTaskIds.add(taskId)
      })
    }
  })

  const taskIdInFilterIndexes = processedFilters.reduce<number[]>((indexes, filter, index) => {
    if (filter.property === 'TaskId' && filter.operation === 'In') {
      indexes.push(index)
    }

    return indexes
  }, [])

  // Collapse TaskId "In" filters to a single intersection so explicit activity filters
  // and custom-data applicability filters do not get OR'ed together on the server.
  if (allCustomDataTaskIds.size > 0 && taskIdInFilterIndexes.length > 0) {
    const intersectedTaskIds = taskIdInFilterIndexes.reduce<Set<number>>(
      (currentIds, filterIndex) =>
        (currentIds as IntersectableSet<number>).intersection(
          new Set(getTaskIdsFromFilterValue(processedFilters[filterIndex].value)),
        ),
      new Set(allCustomDataTaskIds),
    )

    const firstTaskIdInFilter = taskIdInFilterIndexes[0]
    processedFilters[firstTaskIdInFilter] = {
      ...processedFilters[firstTaskIdInFilter],
      value: Array.from(intersectedTaskIds).join(','),
    }

    for (let index = taskIdInFilterIndexes.length - 1; index >= 1; index -= 1) {
      processedFilters.splice(taskIdInFilterIndexes[index], 1)
    }
  } else if (allCustomDataTaskIds.size > 0) {
    processedFilters.push({
      property: 'TaskId',
      kind: 'IntLookup',
      operation: 'In',
      value: Array.from(allCustomDataTaskIds).join(','),
      key: undefined,
    })
  }

  return processedFilters
}

function getTaskIdsFromFilterValue(value: string): number[] {
  return value
    .split(',')
    .map((id) => parseInt(id.trim()))
    .filter((id) => !isNaN(id))
}
